<script setup>
import { computed, ref, watch, onBeforeUnmount, h, Fragment } from 'vue';
import { tooltipState } from './tooltipState.js';

const DEFAULT_TOOLTIP_CLASS = 'bg-gray-900 text-white text-xxs px-2 py-1 rounded shadow-md whitespace-nowrap';

const top = ref(0);
const left = ref(0);

function measure() {
    const el = tooltipState.triggerEl;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    switch (tooltipState.alignment) {
        case 'center':
            left.value = rect.left + rect.width / 2;
            break;
        case 'right':
            left.value = rect.right;
            break;
        default:
            left.value = rect.left;
    }

    switch (tooltipState.verticalAlignment) {
        case 'center':
            top.value = rect.top + rect.height / 2;
            break;
        case 'bottom':
            top.value = rect.bottom + tooltipState.offset;
            break;
        default:
            top.value = rect.top - tooltipState.offset;
    }
}

const transformClass = computed(() => {
    const parts = [];
    switch (tooltipState.alignment) {
        case 'center':
            parts.push('-translate-x-1/2');
            break;
        case 'right':
            parts.push('-translate-x-full');
            break;
    }
    switch (tooltipState.verticalAlignment) {
        case 'center':
            parts.push('-translate-y-1/2');
            break;
        case 'bottom':
            break;
        default:
            parts.push('-translate-y-full');
    }
    return parts.join(' ');
});

const tooltipClass = computed(() => tooltipState.tooltipClass || DEFAULT_TOOLTIP_CLASS);

const SlotRenderer = (props) => h(Fragment, null, props.render());
SlotRenderer.props = ['render'];

function onReposition() {
    if (!tooltipState.visible) return;
    measure();
}

watch(
    () => [tooltipState.visible, tooltipState.triggerEl],
    ([visible]) => {
        if (visible) measure();
    }
);

watch(
    () => tooltipState.visible,
    (visible) => {
        if (visible) {
            window.addEventListener('scroll', onReposition, true);
            window.addEventListener('resize', onReposition);
        } else {
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition);
        }
    }
);

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onReposition, true);
    window.removeEventListener('resize', onReposition);
});
</script>

<template>
    <Teleport to="body">
        <span
            v-if="tooltipState.visible"
            class="fixed z-[9999] pointer-events-none transform"
            :class="[transformClass, tooltipClass, tooltipState.additionalTooltipClass]"
            :style="{ top: top + 'px', left: left + 'px' }"
        >
            <SlotRenderer v-if="tooltipState.render" :render="tooltipState.render" />
            <template v-else>{{ tooltipState.content }}</template>
        </span>
    </Teleport>
</template>
